import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ConfigContext from '../../../../../contexts/ConfigContext';
import CartContext from '../../../../../contexts/CartContext';
import CartUtils from '../../../../../utils/CartUtils';
import { FormControl, FormLabel, TextField } from '@mui/material';
import CountrySelect from '../../../inputs/CountrySelect';

declare global {
  interface Window {
    paysafe: any;
  }
}

const PAYMENT_PROCESSOR_NAME = 'paysafe';
const MERRCO_API_ID = 'paysafeapi';
const PAYSAFE_SDK_URL = 'https://hosted.paysafe.com/checkout/v1/latest/paysafe.checkout.min.js';

// Shape of GET /api/v1/config/paysafe on backroom-server. Mirrors
// PaysafeConfigResponse in backroom-server/src/routes/paysafe_config.rs.
type PaysafeSdkConfig = {
  dev: { username: string; password: string; environment: string };
  prod: { username: string; password: string; environment: string };
};

// btoa() throws DOMException("String contains an invalid character") on
// any codepoint > 0xFF. Wrap it so the user sees WHICH char in WHICH
// field is poisoned — almost always a smart quote / em-dash / non-
// breaking space that snuck in via copy-paste from an email or PDF.
const basicAuthHeader = (id: string, secret: string, fieldNames: { id: string; secret: string }): string => {
  const findBadChars = (s: string) => {
    const out: { index: number; char: string; code: string }[] = [];
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      if (c > 0xFF) out.push({ index: i, char: s[i], code: 'U+' + c.toString(16).toUpperCase().padStart(4, '0') });
    }
    return out;
  };
  const idBad = findBadChars(id);
  const secretBad = findBadChars(secret);
  if (idBad.length || secretBad.length) {
    const parts: string[] = [];
    if (idBad.length) parts.push(`${fieldNames.id} has non-Latin1 char(s): ${idBad.map(b => `pos ${b.index} (${b.code} "${b.char}")`).join(', ')}`);
    if (secretBad.length) parts.push(`${fieldNames.secret} has non-Latin1 char(s): ${secretBad.map(b => `pos ${b.index} (${b.code} "${b.char}")`).join(', ')}`);
    throw new Error(`Paysafe credential is not valid for HTTP Basic auth. ${parts.join('; ')}. Most likely a smart quote, em-dash, or non-breaking space pasted from an email — re-type the value in config.toml.`);
  }
  return btoa(`${id}:${secret}`);
};

const loadPaysafeApi = (callback: () => void) => {
  const existing = document.getElementById(MERRCO_API_ID);
  if (existing) {
    callback();
    return;
  }
  const script = document.createElement('script');
  script.src = PAYSAFE_SDK_URL;
  script.id = MERRCO_API_ID;
  script.onload = callback;
  document.body.appendChild(script);
};

type Props = {
  setGetPaymentToken: React.Dispatch<React.SetStateAction<undefined | (() => () => Promise<any>)>>;
}

const PaysafePaymentProvider = ({setGetPaymentToken}: Props) => {
  const {config} = useContext(ConfigContext);
  const COMPANY = config.general.product_name;
  const ROOT = config.general.root_category;

  const [paysafeLoaded, setPaysafeLoaded] = useState(false);

  const {cart, setCartState} = useContext(CartContext);

  const cartRef: any = useRef();
  cartRef.current = cart;

  // Paysafe SDK creds come from backroom-server's config.toml (not Mongo).
  // Fetched once at mount; held in a ref so the checkout closure sees
  // the latest value without re-registering getPaymentToken every render.
  const paysafeSdkConfigRef = useRef<PaysafeSdkConfig | null>(null);

  useEffect(() => {
    const apiHost = process.env.REACT_APP_API_HOST as string;
    axios.get<PaysafeSdkConfig>(`${apiHost}/api/v1/config/paysafe`)
      .then(resp => { paysafeSdkConfigRef.current = resp.data; })
      .catch(err => { console.error('[PaysafePaymentProvider] failed to load /api/v1/config/paysafe', err); });
  }, []);

  useEffect(() => {
    loadPaysafeApi(() => setPaysafeLoaded(true));
  }, []);

  useEffect(() => {
    if (paysafeLoaded) {
      setGetPaymentToken(() => checkout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paysafeLoaded]);

  // Pre-fill the cardholder address. customerAddress is the customer's
  // profile (name/email), NOT a postal address — the real address lives
  // on deliveryAddress. Heuristic: if the customer is buying for self
  // (customer name == delivery name), the delivery address IS the
  // cardholder's; otherwise it's a gift and we don't know the buyer's
  // address. Any field the user has already touched on cardholderAddress
  // wins.
  useEffect(() => {
    const buyingForSelf =
      cart.customerAddress?.firstName === cart.deliveryAddress?.firstName &&
      cart.customerAddress?.lastName === cart.deliveryAddress?.lastName;

    const defaultStreet     = buyingForSelf ? cart.deliveryAddress?.street     : '';
    const defaultAdditional = buyingForSelf ? cart.deliveryAddress?.additional : '';
    const defaultCity       = buyingForSelf ? cart.deliveryAddress?.city       : '';
    const defaultProvince   = buyingForSelf ? cart.deliveryAddress?.province   : '';
    const defaultPostal     = buyingForSelf ? cart.deliveryAddress?.postal     : '';

    const ch = cart.cardholderAddress ?? {};
    setCartState({
      ...cart,
      cardholderAddress: {
        firstName:  ch.firstName  || cart.customerAddress?.firstName || '',
        lastName:   ch.lastName   || cart.customerAddress?.lastName  || '',
        street:     ch.street     || defaultStreet     || '',
        additional: ch.additional || defaultAdditional || '',
        city:       ch.city       || defaultCity       || '',
        province:   ch.province   || defaultProvince   || '',
        country:    ch.country    || '',
        postal:     ch.postal     || defaultPostal     || '',
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCardholderAddressField = (name: string, value: string) => {
    setCartState({
      ...cart,
      cardholderAddress: {
        ...cart.cardholderAddress,
        [name]: value
      }
    });
  }

  const onFirstNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCardholderAddressField('firstName', event.target.value);
  }

  const onLastNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCardholderAddressField('lastName', event.target.value);
  }

  const onCountryChanged = (code: string) => {
    updateCardholderAddressField('country', code);
  }

  const onPostalChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCardholderAddressField('postal', event.target.value.toUpperCase());
  }

  const checkout = async (): Promise<any> => {
    // Rebuild from cartRef.current — the closure captured by
    // setGetPaymentToken at mount sees a stale cart.
    const cart = cartRef.current;
    const freshUtils = new CartUtils(cart);

    const sdkConfig = paysafeSdkConfigRef.current;
    if (!sdkConfig) {
      return Promise.reject('Paysafe SDK config not loaded yet — try again in a moment.');
    }

    const test = (freshUtils.getEnvironment() === "TEST");
    const USERNAME = test ? sdkConfig.dev.username : sdkConfig.prod.username;
    const PASSWORD = test ? sdkConfig.dev.password : sdkConfig.prod.password;
    const PAYSAFE_ENVIRONMENT = test ? sdkConfig.dev.environment : sdkConfig.prod.environment;

    if (!USERNAME || !PASSWORD) {
      return Promise.reject(`Paysafe ${test ? 'dev' : 'prod'} SDK creds not set on the server (config.toml [paysafe.${test ? 'dev' : 'prod'}] sdk_username/sdk_password).`);
    }

    let CREDENTIALS: string;
    try {
      CREDENTIALS = basicAuthHeader(USERNAME, PASSWORD, { id: 'sdk_username', secret: 'sdk_password' });
    } catch (e: any) {
      return Promise.reject(e.message);
    }

    const total = await freshUtils.getTotal();
    if (total === undefined || total <= 0) {
      return Promise.reject('Cart total is not available.');
    }

    return new Promise((resolve: any, reject: any) => {
      window.paysafe.checkout.setup(CREDENTIALS, {
        holderName: `${cart.cardholderAddress.firstName} ${cart.cardholderAddress.lastName}`,
        billingAddress: {
          country: cart.cardholderAddress.country,
          zip: cart.cardholderAddress.postal
        },
        amount: total,
        currency: "CAD",
        environment: PAYSAFE_ENVIRONMENT,
        companyName: test ? `${COMPANY} TEST` : COMPANY,
        imageUrl: `https://thequeensflorist.sirv.com/${ROOT}/logos/icon-small.png`
      }, function(instance: any, error: any, result: any) {
        if (error) {
          console.error('[PaysafePaymentProvider] SDK error', error);
          reject(error);
          return;
        }
        if (result?.token) {
          resolve({
            provider: PAYMENT_PROCESSOR_NAME,
            location: PAYMENT_PROCESSOR_NAME,
            method: result.paymentMethod,
            token: result.token,
            environment: PAYSAFE_ENVIRONMENT,
            timestamp: new Date(),
            callback: (success: boolean) => {
              if (success) {
                instance.close();
              } else {
                setCartState({
                  ...cartRef.current,
                  lastError: 'Payment was declined. Try again with the same or another payment method.'
                });
                instance.close();
                reject('Payment was declined. Try again with the same or another payment method.');
              }
            }
          });
        }
      }, function(stage: any) {
        if (stage === "BeforePayment") {
          setCartState({ ...cartRef.current, lastError: 'Request cancelled by user.' });
          reject('Request cancelled by user.');
        } else if (stage === "DuringPayment") {
          setCartState({ ...cartRef.current, lastError: 'Request cancelled by user before completion of payment.' });
          reject('Request cancelled by user before completion of payment.');
        }
        // AfterPayment: closed via instance.close or from the success
        // screen — nothing to do.
      });
    });
  }

  if (!paysafeLoaded) {
    return <>Contacting payment processor...</>;
  }

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend" className='checkout-form-legend'>Cardholder details</FormLabel>
      <div>
        Card payments will be performed securely through <a href="https://www.paysafe.com/ca-en/" target="_blank" rel="noreferrer">Paysafe</a>
      </div>
      <div className='checkout-form-fields'>
        <TextField required id="firstName" label="First Name" value={cart.cardholderAddress?.firstName ?? ''} onChange={onFirstNameChanged}/>
        <TextField required id="lastName" label="Last Name" value={cart.cardholderAddress?.lastName ?? ''} onChange={onLastNameChanged}/>
        <CountrySelect onChange={onCountryChanged}/>
        <FormControl>
          <TextField
            label="Postal/Zip Code"
            value={cart.cardholderAddress?.postal ?? ''}
            onChange={onPostalChanged}
            name="postal"
            id="postal"
          />
        </FormControl>
      </div>
    </FormControl>
  );
}

export default PaysafePaymentProvider;
